import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import usePush from 'hooks/router/usePush';
import { getMenu } from 'components/Navigation';

const NavigationBar = () => {
  const user = useSelector(state => state.auth.user);
  const admin = useSelector(state => state.auth.admin);
  const { pathname } = useLocation();
  const screenWidth = useSelector(state => state.client.screenWidth);
  const tenantTheme = useSelector(state => state.tenant.theme);
  const tenantId = useSelector(state => state.tenant.id);
  const { push } = usePush();

  const { formatMessage } = useIntl();

  // Guard tenantTheme.maxWidth: missing or non-numeric values would produce NaN
  // contentWidth and break the inline maxWidth string ("NaNpx").
  const safeMaxWidth = Number.isFinite(tenantTheme?.maxWidth) ? tenantTheme.maxWidth : 1200;
  const contentWidth = Math.min(screenWidth || safeMaxWidth, safeMaxWidth);
  const innerBandStyle = {
    margin: '0 auto',
    maxWidth: `${contentWidth}px`,
    padding: '0 16px',
    width: '100%',
  };

  // TODO For now, we will have to control Tab's activeIndex manually with the information we get from react-router
  // Long-term solution, might be to upgrade to RRv4. With that, basically every component can have a route, so we could potentially utilize that
  const filterPanes = (panes, acls) => {
    const userAcls = acls || [];
    const panesCopy = panes.map(pane => {
      const filteredChildren = pane.children ? filterPanes(pane.children, userAcls) : [];
      return { ...pane, children: filteredChildren };
    });
    return panesCopy.filter(pane => !pane.acl || pane.acl.some(level => userAcls.includes(level)));
  };

  const panes = getMenu(formatMessage, user, admin, tenantId);
  const userPanes = filterPanes(panes, user && user.acl);

  const escapeRegExp = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const routeMatches = url => {
    if (!url) {
      return false;
    }
    if (pathname === url) {
      return true;
    }
    // Boundary-aware match: the url must appear in pathname surrounded by path-segment
    // boundaries. This handles the locale prefix (e.g. /en/financial/x matches /financial/x)
    // while preventing false positives like /booking matching /book.
    const stripped = url.replace(/^\//, '');
    if (!stripped) {
      return false;
    }
    return new RegExp(`(^|/)${escapeRegExp(stripped)}(/|$)`).test(pathname);
  };

  const activeIndex = userPanes.findIndex(p => {
    if (routeMatches(p.url)) {
      return true;
    }
    if (p.children) {
      return p.children.find(subPane => routeMatches(subPane.url));
    }
    return false;
  });
  const activePane = userPanes[activeIndex >= 0 ? activeIndex : 0];
  let subPanes = [];
  if (activePane && activePane.children && activePane.children.length > 0) {
    subPanes = activePane.children;
  }
  else if (activePane) {
    subPanes = [activePane];
  }

  // Defence in depth: redux-driven screenType detection hides this component on
  // tablet/mobile via App.js, but on first paint screenType defaults to "desktop"
  // and the desktop nav can flash on a narrow viewport. The hidden lg:block class
  // ensures the nav never paints below 1024 px regardless of redux state.
  return (
    <nav aria-label="Main navigation" className="hidden lg:block" style={{ width: '100%' }}>
      <div style={{ background: 'var(--owner-nav-bg)', width: '100%' }}>
        <div
          style={{
            ...innerBandStyle,
            alignItems: 'stretch',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            overflow: 'visible',
            paddingBottom: 0,
            paddingTop: 0,
          }}
        >
          {userPanes.map((pane, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={pane.title}
                type="button"
                onClick={() => push(pane.url)}
                className={isActive ? '' : 'hover:bg-white/[0.06]'}
                style={{
                  background: isActive ? 'var(--owner-nav-active-bg)' : undefined,
                  border: 0,
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                  fontSize: '15px',
                  fontWeight: isActive ? 700 : 600,
                  lineHeight: '20px',
                  padding: '16px 20px 14px',
                  position: 'relative',
                  transition: 'background-color 160ms ease, color 160ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {pane.title}
                {isActive && (
                  <span
                    aria-hidden="true"
                    style={{
                      background: '#FFFFFF',
                      bottom: 0,
                      borderTopLeftRadius: '2px',
                      borderTopRightRadius: '2px',
                      height: '2px',
                      left: '20px',
                      position: 'absolute',
                      right: '20px',
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ background: 'var(--owner-subnav-bg)', width: '100%' }}>
        <div
          style={{
            ...innerBandStyle,
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            minHeight: '52px',
            overflow: 'visible',
            paddingBottom: '10px',
            paddingTop: '10px',
          }}
        >
          {subPanes.map(sub => {
            const isActive = routeMatches(sub.url);
            return (
              <button
                key={sub.title}
                type="button"
                onClick={() => sub.url && push(sub.url)}
                className={isActive || !sub.url ? '' : 'hover:bg-white/[0.10]'}
                style={{
                  background: isActive ? 'rgba(255,255,255,0.18)' : undefined,
                  border: 0,
                  borderRadius: '999px',
                  color: '#FFFFFF',
                  cursor: sub.url ? 'pointer' : 'default',
                  fontSize: '14px',
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: '20px',
                  padding: '6px 14px',
                  transition: 'background-color 160ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {sub.title}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default NavigationBar;
